import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionSM, LabelSM, TitleXXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { getWebClient } from '@/services/ApolloClient'
import { useSaleBanner } from '@/services/ContentfulSaleService/hooks'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

export interface TicketsNavBannerProps {
  hide?: boolean
}

export const TicketsNavBanner: React.FC<TicketsNavBannerProps> = ({ hide = false }) => {
  const track = useSafeTrack()
  const { t } = useTranslate('common')
  const router = useSafeRouter()
  const { loading: bannerLoading, saleWithBanner } = useSaleBanner()
  const { isGuildMember, loading } = useGuildMember()
  const client = getWebClient()
  const [shouldRender, setShouldRender] = useState(false)
  const [activeBanner, setActiveBanner] = useState<'david' | 'iWasAStranger' | null>(null)
  const { numberAvailable: davidGuildTicketsCount = 0, loading: loadingDavidGuildTickets } = useUserGuildTickets({
    theatricalSlug: slugs.david,
    client,
    skip: !isGuildMember || activeBanner !== 'david',
  })
  const { numberAvailable: iWasAStrangerGuildTicketsCount = 0, loading: loadingIWasAStrangerGuildTickets } =
    useUserGuildTickets({
      theatricalSlug: slugs.iWasAStranger,
      client,
      skip: !isGuildMember || activeBanner !== 'iWasAStranger',
    })

  const guildTicketsCount = activeBanner === 'david' ? davidGuildTicketsCount : iWasAStrangerGuildTicketsCount
  const loadingGuildTickets = activeBanner === 'david' ? loadingDavidGuildTickets : loadingIWasAStrangerGuildTickets

  useEffect(() => {
    const hasDismissedDavidBanner = getBooleanFromLocalStorage('hasDismissedDavidNavBanner') ?? false
    const hasDismissedIWasAStrangerBanner = getBooleanFromLocalStorage('hasDismissedIWasAStrangerNavBanner') ?? false
    const isGuildJoinPage = router.pathname.includes('/guild/join')
    const isGuildPricingPage = router.pathname.includes('/guild/pricing')
    const isGuildCheckoutPage = router.pathname.includes('/guild/checkout')
    const isMarketingOfferPage = router.pathname.includes('/m/offer')

    if (hide || isGuildJoinPage || isGuildPricingPage || isGuildCheckoutPage || isMarketingOfferPage) {
      setShouldRender(false)
      setActiveBanner(null)
    } else if (!loading && !bannerLoading && (isGuildMember || !saleWithBanner)) {
      // Show DAVID banner first if not dismissed
      if (!hasDismissedDavidBanner) {
        setShouldRender(true)
        setActiveBanner('david')
      }
      // Fall back to I Was a Stranger if DAVID is dismissed but I Was a Stranger is not
      else if (!hasDismissedIWasAStrangerBanner) {
        setShouldRender(true)
        setActiveBanner('iWasAStranger')
      }
      // Both dismissed, show nothing
      else {
        setShouldRender(false)
        setActiveBanner(null)
      }
    }
  }, [hide, router.pathname, isGuildMember, loading, bannerLoading, saleWithBanner])

  const handleTicketsNavBannerDismiss = useCallback(
    (buttonOrigin: boolean, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (activeBanner === 'david') {
        writeToLocalStorage('hasDismissedDavidNavBanner', true)
        if (!buttonOrigin) {
          track('Tickets Nav Banner Dismissed', { banner: 'DAVID' })
        }
        // Check if we should show I Was a Stranger fallback
        const hasDismissedIWasAStrangerBanner = getBooleanFromLocalStorage('hasDismissedIWasAStrangerNavBanner')
        if (!hasDismissedIWasAStrangerBanner) {
          setActiveBanner('iWasAStranger')
          setShouldRender(true)
          return
        }
        setShouldRender(false)
        setActiveBanner(null)
      } else if (activeBanner === 'iWasAStranger') {
        writeToLocalStorage('hasDismissedIWasAStrangerNavBanner', true)
        if (!buttonOrigin) {
          track('Tickets Nav Banner Dismissed', { banner: 'I Was a Stranger' })
        }
        setShouldRender(false)
        setActiveBanner(null)
      }
    },
    [activeBanner, track],
  )

  const handleTicketsNavButtonClick = useCallback(() => {
    track('Tickets Nav Banner Button Clicked', { banner: activeBanner === 'david' ? 'DAVID' : 'I Was a Stranger' })
    handleTicketsNavBannerDismiss(true)
  }, [activeBanner, handleTicketsNavBannerDismiss, track])

  return (
    <When condition={shouldRender}>
      <a
        className={classNames(
          'fixed cursor-pointer md:absolute inset-x-0 bottom-0 md:bottom-auto md:top-0 z-40 md:z-50 flex flex-col items-center max-w-full group-floating-cta',
          activeBanner === 'david' ? 'bg-[#010D2E]' : 'bg-[#012A39]',
        )}
        onClick={handleTicketsNavButtonClick}
        href={`${paths.tickets.index}/${activeBanner === 'david' ? slugs.david : slugs.iWasAStranger}`}
      >
        <div className="responsive relative h-14 w-full max-w-[1728px] p-3 md:h-16 md:p-4">
          <ResponsiveBackgroundImage
            src={getCloudinaryImageUrl({
              path:
                activeBanner === 'david'
                  ? '/v1761769740/angel-studios/nav/Promotional-Banner-BG-Mobile-Image-01_2.webp'
                  : '/v1765811740/angel-studios/nav/mobile-i-was-a-stranger-banner.webp',
              width: 1024,
            })}
            className="absolute inset-0 h-full w-full lg:hidden"
          />
          <ResponsiveBackgroundImage
            src={getCloudinaryImageUrl({
              path:
                activeBanner === 'david'
                  ? '/v1761768402/angel-studios/nav/Promotional-Banner-DAVID-BG-Desk-Image-01_2.webp'
                  : '/v1765811770/angel-studios/nav/desktop-i-was-a-stranger-banner.webp',
              width: 1728,
            })}
            className="absolute inset-0 hidden h-full w-full bg-cover bg-no-repeat lg:block"
          />
          <div className="relative mx-auto flex h-full w-full max-w-[1728px] items-center justify-between">
            <div className="flex flex-row md:gap-[27px]">
              <div className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-[27px]">
                <Image
                  src={getCloudinaryImageUrl({
                    path:
                      activeBanner === 'david'
                        ? '/v1761674532/angel-studios/nav/DAVID_MOVIE_David_Logo_MAIN_LOGO_ENG_2025_10_1.webp'
                        : '/v1765812377/angel-studios/nav/6ceb5127-833a-4cc1-91fd-cb09551aea91.webp',
                    width: 129,
                  })}
                  alt={activeBanner === 'david' ? 'DAVID Horizontal Logo' : 'I Was a Stranger Horizontal Logo'}
                  className="max-h-[16px] max-w-[64px] md:max-h-[32px] md:max-w-[129px]"
                  width={129}
                  height={32}
                />
                <CaptionSM
                  weight="bold"
                  className={classNames(
                    'lg:photon-title-xs [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)] lg:[text-shadow:none]',
                    activeBanner === 'david' ? '!text-[#E4975D]' : '!text-white',
                  )}
                >
                  {t('ticketsAvailableNow', 'Tickets Available Now')}
                </CaptionSM>
              </div>
              <Image
                src={getCloudinaryImageUrl({
                  path:
                    activeBanner === 'david'
                      ? '/v1761674708/angel-studios/nav/Promo_Tickets.webp'
                      : '/v1765812197/angel-studios/nav/Promo_Tickets_Stranger.webp',
                  width: 104,
                })}
                alt={activeBanner === 'david' ? 'DAVID Tickets' : 'I Was a Stranger Tickets'}
                className="hidden md:block"
                width={104}
                height={48}
              />
            </div>
            <div className="flex flex-row items-center gap-[35px] md:gap-[42px] lg:gap-[145px]">
              <div>
                <When condition={!loadingGuildTickets}>
                  <LinkButton
                    href={`${paths.tickets.index}/${activeBanner === 'david' ? slugs.david : slugs.iWasAStranger}`}
                    variant={'black'}
                    className="rounded-lg border-transparent bg-white/20 px-4 py-2 hover:bg-white/40"
                    onClick={handleTicketsNavButtonClick}
                  >
                    <LabelSM>
                      {guildTicketsCount && guildTicketsCount > 0
                        ? t('useGuildTickets', 'Use Guild Tickets')
                        : t('getShowtimes', 'Get Showtimes')}
                    </LabelSM>
                  </LinkButton>
                </When>
              </div>
              <div
                onClick={(e) => handleTicketsNavBannerDismiss(false, e)}
                className="cursor-pointer rounded-full lg:rounded-none"
              >
                <div className="hidden cursor-pointer lg:flex lg:flex-row lg:items-center lg:gap-2">
                  <TitleXXS weight="semibold" color="core-gray-400">
                    {t('close', 'Close')}
                  </TitleXXS>
                  <CloseIcon color="core-gray-400" size={24} className="cursor-pointer" />
                </div>
                <div className="lg:hidden">
                  <CircleCloseIcon color="core-gray-400" size={21} className="cursor-pointer" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </a>

      {/* Spacer to push the site nav below the fixed banner on md+ */}
      <div className="hidden h-16 w-full md:block" aria-hidden="true" />
    </When>
  )
}
